/**
 * TaskModal Component
 *
 * Reusable modal for viewing and editing tasks.
 * Can be used across Admin Dashboard, Admin Projects, and other pages.
 * Fetches task data when taskId is provided, supports create mode when null.
 *
 * @component
 * @version 1.0.0
 * @created 2025-12-12
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  FaTimes,
  FaEdit,
  FaPlus,
  FaCheckCircle,
  FaPlusCircle,
  FaSpinner,
  FaTrash,
  FaExclamationTriangle,
  FaCalendarAlt,
  FaClipboardList,
  FaHome,
  FaHardHat,
  FaFileAlt,
  FaStickyNote,
  FaLock,
  FaPaperclip,
  FaBuilding,
  FaCloudUploadAlt,
  FaQuestionCircle,
  FaNewspaper,
  FaImage,
  FaInfoCircle,
} from "react-icons/fa";
import { useQueryClient } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import {
  useTaskForEdit,
  useUpdateTask,
  useCreateTask,
  useDeleteTask,
} from "../../hooks/useTasks";
import { useActiveVendors } from "../../hooks/useVendors";
import { useActiveProjects } from "../../hooks/useProjects";
import {
  uploadFile,
  buildUploadContext,
} from "../../services/cloudinaryService";
import { functions } from "../../firebase";
import useCurrentTeamMember from "../../hooks/useCurrentTeamMember";
import ConfirmationModal from "../ConfirmationModal";
import "./TaskModal.css";

/**
 * Construction phase options matching Airtable schema
 */
const CONSTRUCTION_PHASES = [
  "Planning/Permitting",
  "Foundation",
  "Framing",
  "Rough-In",
  "Insulation & Drywall",
  "Trim & Paint",
  "Mechanical Trim",
  "Final Finishes",
  "Punch List",
  "Final Inspection / CO",
  "Complete",
];

/**
 * Task status options matching Airtable schema
 */
const TASK_STATUSES = ["Not started", "In progress", "Delayed", "Completed"];

/**
 * Maximum file size in bytes (10MB)
 */
const MAX_FILE_SIZE = 10 * 1024 * 1024;

/**
 * Accepted MIME types for task-only Attachments (documents + flat images).
 * These go on the task record's ATTACHMENTS field and do NOT surface in
 * Progress Photos.
 */
const ATTACHMENT_ACCEPT = ".pdf,.jpg,.jpeg,.png";
const ATTACHMENT_MIME_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
]);

/**
 * Accepted MIME types for Files & Photos uploads. These create linked
 * Files & Photos records (type: "Progress Photo") so they also surface
 * in Progress Photos.
 */
const FILES_PHOTOS_ACCEPT = ".jpg,.jpeg,.png,.webp,.heic,.heif";
const FILES_PHOTOS_MIME_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
]);

function formatFileSize(bytes) {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

function validateFile(file, allowedMimeTypes) {
  if (file.size > MAX_FILE_SIZE) {
    return `File too large (${formatFileSize(file.size)}). Max 10MB.`;
  }
  if (allowedMimeTypes && !allowedMimeTypes.has(file.type)) {
    return `Unsupported file type (${file.type || "unknown"}).`;
  }
  return null;
}

/**
 * TaskModal Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Callback when modal closes
 * @param {string|null} props.taskId - Task record ID (null for create mode)
 * @param {string|null} props.projectId - Project ID for new tasks
 * @param {string} [props.projectName] - Project name for display when projectId is provided
 * @param {Function} props.onSave - Callback after successful save
 * @param {Function} props.onDelete - Callback after successful delete
 * @returns {JSX.Element|null} TaskModal component
 */
function TaskModal({
  isOpen,
  onClose,
  taskId = null,
  projectId = null,
  projectName: initialProjectName,
  onSave,
  onDelete,
  showTestAccounts = false,
}) {
  // Determine if this is edit mode or create mode
  const isEditMode = Boolean(taskId);

  // Fetch task data if in edit mode
  const {
    data: taskData,
    isLoading: taskLoading,
    error: taskError,
  } = useTaskForEdit(taskId);

  // Fetch vendors for dropdown
  const { data: vendors, isLoading: vendorsLoading } = useActiveVendors();

  // Fetch active projects for dropdown (create mode)
  const { data: projects, isLoading: projectsLoading } = useActiveProjects({
    showTestAccounts,
  });

  // Mutations
  const updateTask = useUpdateTask();
  const createTask = useCreateTask();
  const deleteTask = useDeleteTask();
  const queryClient = useQueryClient();

  // Canonical RSH team member name for Cloudinary `uploaded_by_name`
  const { name: teamMemberName } = useCurrentTeamMember();

  // Local form state
  const [formData, setFormData] = useState({
    taskName: "",
    taskDescription: "",
    status: "Not started",
    constructionPhase: "",
    startDate: "",
    endDate: "",
    dateCompleted: "",
    taskNotes: "",
    internalNotes: "",
    includeInDigest: false,
    assignedVendor: "",
    workType: "",
  });

  const [isDirty, setIsDirty] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Task-only Attachments upload state (ATTACHMENTS field)
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const fileInputRef = useRef(null);

  // Files & Photos upload state (creates linked Files & Photos records)
  const [pendingPhotoFiles, setPendingPhotoFiles] = useState([]);
  const [isDraggingPhotos, setIsDraggingPhotos] = useState(false);
  const [photoUploadProgress, setPhotoUploadProgress] = useState({});
  const photoInputRef = useRef(null);
  const [photoName, setPhotoName] = useState("");
  const [individualPhotoNames, setIndividualPhotoNames] = useState(false);
  const [photoFileNames, setPhotoFileNames] = useState({});
  const [photoNameError, setPhotoNameError] = useState("");

  // Validation error for the conditionally-required Completion Date field
  const [dateCompletedError, setDateCompletedError] = useState("");

  // Populate form when task data loads
  useEffect(() => {
    if (taskData && isEditMode) {
      setFormData({
        taskName: taskData.taskName || "",
        taskDescription: taskData.taskDescription || "",
        status: taskData.status || "Not started",
        constructionPhase: taskData.constructionPhase || "",
        startDate: taskData.startDate || "",
        endDate: taskData.endDate || "",
        dateCompleted: taskData.dateCompleted || "",
        taskNotes: taskData.taskNotes || "",
        internalNotes: taskData.internalNotes || "",
        includeInDigest: taskData.includeInDigest || false,
        assignedVendor: taskData.assignedVendorId || "",
        workType: taskData.workType || "",
      });
      setIsDirty(false);
      setPendingFiles([]);
      setPendingPhotoFiles([]);
      setUploadProgress({});
      setPhotoUploadProgress({});
      setPhotoName("");
      setIndividualPhotoNames(false);
      setPhotoFileNames({});
      setPhotoNameError("");
      setDateCompletedError("");
    }
  }, [taskData, isEditMode]);

  // Reset form when modal opens in create mode
  useEffect(() => {
    if (isOpen && !isEditMode) {
      setFormData({
        taskName: "",
        taskDescription: "",
        status: "Not started",
        constructionPhase: "",
        startDate: "",
        endDate: "",
        dateCompleted: "",
        taskNotes: "",
        internalNotes: "",
        includeInDigest: false,
        assignedVendor: "",
        workType: "",
        projectId: projectId || "",
      });
      setIsDirty(false);
      setPendingFiles([]);
      setPendingPhotoFiles([]);
      setUploadProgress({});
      setPhotoUploadProgress({});
      setPhotoName("");
      setIndividualPhotoNames(false);
      setPhotoFileNames({});
      setPhotoNameError("");
      setDateCompletedError("");
    }
  }, [isOpen, isEditMode, projectId]);

  const handleFilesSelected = useCallback((files) => {
    const newFiles = Array.from(files).map((file) => ({
      file,
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      preview: file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : null,
      error: validateFile(file, ATTACHMENT_MIME_TYPES),
    }));
    setPendingFiles((prev) => [...prev, ...newFiles]);
    setIsDirty(true);
  }, []);

  const handlePhotoFilesSelected = useCallback((files) => {
    const newFiles = Array.from(files).map((file) => ({
      file,
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      preview: file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : null,
      error: validateFile(file, FILES_PHOTOS_MIME_TYPES),
    }));
    setPendingPhotoFiles((prev) => [...prev, ...newFiles]);
    setIsDirty(true);
  }, []);

  const removePendingFile = (fileId) => {
    setPendingFiles((prev) => {
      const file = prev.find((f) => f.id === fileId);
      if (file?.preview) URL.revokeObjectURL(file.preview);
      return prev.filter((f) => f.id !== fileId);
    });
  };

  const removePendingPhotoFile = (fileId) => {
    setPendingPhotoFiles((prev) => {
      const file = prev.find((f) => f.id === fileId);
      if (file?.preview) URL.revokeObjectURL(file.preview);
      return prev.filter((f) => f.id !== fileId);
    });
    setPhotoFileNames((prev) => {
      if (!(fileId in prev)) return prev;
      const next = { ...prev };
      delete next[fileId];
      return next;
    });
  };

  // Drag and drop handlers (Attachments)
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files?.length > 0) {
        handleFilesSelected(e.dataTransfer.files);
      }
    },
    [handleFilesSelected],
  );

  // Drag and drop handlers (Files & Photos)
  const handlePhotoDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDraggingPhotos(true);
  }, []);

  const handlePhotoDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDraggingPhotos(false);
  }, []);

  const handlePhotoDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDraggingPhotos(false);
      if (e.dataTransfer.files?.length > 0) {
        handlePhotoFilesSelected(e.dataTransfer.files);
      }
    },
    [handlePhotoFilesSelected],
  );

  // Handle input changes
  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // When the user switches status to Completed, blank the completion
      // date so they must consciously enter the actual completion date
      // (rather than inheriting a tentative End Date).
      if (
        field === "status" &&
        value === "Completed" &&
        prev.status !== "Completed"
      ) {
        next.dateCompleted = "";
      }
      return next;
    });
    if (field === "dateCompleted" && dateCompletedError) {
      setDateCompletedError("");
    }
    setIsDirty(true);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    // A completed task must have an explicit completion date.
    if (formData.status === "Completed" && !formData.dateCompleted) {
      setDateCompletedError("Please enter the task completion date.");
      return;
    }

    try {
      // Use field IDs from airtableFields.js for consistency with backend
      const FIELDS = {
        TASKS: {
          TASK_NAME: "fldKWGpDAqZoV1Opo",
          TASK_DESCRIPTION: "fldgm6VNeqsElJEk0",
          STATUS: "fldO5gvRR7YypTGKu",
          CONSTRUCTION_PHASE: "fldB904M1E3pruQEY",
          START_DATE: "fldWDOtLOzmgv6YNJ",
          END_DATE: "fldHUY5SeKWRFGSku",
          DATE_COMPLETED: "fldoZXyzBOm03fFzS",
          TASK_NOTES: "fldxuLucZNtfHEzzQ",
          INTERNAL_NOTES: "fldUZEzHiEhXcKObO",
          INCLUDE_IN_DIGEST: "fldeipwnkyiDBqQ4D",
          ASSIGNED_VENDOR: "fldoOH5zFRz15o39H",
          PROJECT: "fldZtzb6XHiGmUqMI",
          ATTACHMENTS: "fldAf3aG2vwwhLsxw",
        },
      };

      // Upload pending files to Cloudinary
      const validFiles = pendingFiles.filter((f) => !f.error);
      const uploadedAttachments = [];

      if (validFiles.length > 0) {
        // Resolve project context for standardized Cloudinary metadata.
        // Edit mode: pull from fetched taskData. Create mode: prefer the
        // user's dropdown selection, then the prop, then the loaded
        // projects list as a last resort for project_name.
        const resolvedProjectId = isEditMode
          ? taskData?.projectId || ""
          : formData.projectId || projectId || "";
        const matchedProject = projects?.find(
          (p) => p.id === resolvedProjectId,
        );
        const resolvedProjectName = isEditMode
          ? taskData?.projectName || ""
          : initialProjectName ||
            matchedProject?.projectName ||
            matchedProject?.name ||
            "";

        for (let i = 0; i < validFiles.length; i++) {
          const { file } = validFiles[i];
          setUploadProgress((prev) => ({
            ...prev,
            [validFiles[i].id]: { status: "uploading", progress: 0 },
          }));

          // Build standardized Cloudinary context for this upload
          const uploadContext = buildUploadContext({
            projectId: resolvedProjectId,
            projectName: resolvedProjectName,
            category: "attachments",
            categoryLabel: "Task Attachments",
            sourceTable: "tasks",
            uploadedByName: teamMemberName,
            extras: {
              task_id: isEditMode ? taskId : "",
            },
            file,
          });

          const cloudinaryResult = await uploadFile(file, {
            projectId: resolvedProjectId,
            sourceTable: "tasks",
            category: "attachments",
            uploadContext,
            onProgress: (progress) => {
              setUploadProgress((prev) => ({
                ...prev,
                [validFiles[i].id]: { status: "uploading", progress },
              }));
            },
          });

          uploadedAttachments.push({
            url: cloudinaryResult.url,
            filename: file.name,
          });

          setUploadProgress((prev) => ({
            ...prev,
            [validFiles[i].id]: { status: "success", progress: 100 },
          }));
        }
      }

      const taskFields = {
        [FIELDS.TASKS.TASK_NAME]: formData.taskName,
        [FIELDS.TASKS.TASK_DESCRIPTION]: formData.taskDescription,
        [FIELDS.TASKS.STATUS]: formData.status,
        [FIELDS.TASKS.CONSTRUCTION_PHASE]: formData.constructionPhase || null,
        [FIELDS.TASKS.START_DATE]: formData.startDate || null,
        [FIELDS.TASKS.END_DATE]: formData.endDate || null,
        [FIELDS.TASKS.TASK_NOTES]: formData.taskNotes,
        [FIELDS.TASKS.INTERNAL_NOTES]: formData.internalNotes?.trim() || "",
        [FIELDS.TASKS.INCLUDE_IN_DIGEST]: formData.includeInDigest,
      };

      // Completion date: only sent for completed tasks
      if (formData.status === "Completed") {
        taskFields[FIELDS.TASKS.DATE_COMPLETED] = formData.dateCompleted;
      }

      // Add vendor if selected
      if (formData.assignedVendor) {
        taskFields[FIELDS.TASKS.ASSIGNED_VENDOR] = [formData.assignedVendor];
      }

      // Add attachments: keep existing + add new uploads
      if (uploadedAttachments.length > 0) {
        const existingAttachments = (taskData?.attachments || []).map(
          (att) => ({
            url: att.url,
            filename: att.filename,
          }),
        );
        taskFields[FIELDS.TASKS.ATTACHMENTS] = [
          ...existingAttachments,
          ...uploadedAttachments,
        ];
      }

      if (isEditMode) {
        await updateTask.mutateAsync({
          taskId,
          updates: taskFields,
        });
      } else {
        // Add project link for new tasks
        if (projectId) {
          taskFields[FIELDS.TASKS.PROJECT] = [projectId];
        }
        await createTask.mutateAsync(taskFields);
      }

      // Files & Photos uploads only apply in edit mode (need existing taskId
      // and projectId to link the created records).
      const validPhotoFiles = pendingPhotoFiles.filter((f) => !f.error);
      if (isEditMode && validPhotoFiles.length > 0) {
        // Validate photo name requirements before uploading anything
        if (individualPhotoNames) {
          const missing = validPhotoFiles.some(
            (f) => !photoFileNames[f.id]?.trim(),
          );
          if (missing) {
            setPhotoNameError("Enter a name for each photo.");
            return;
          }
        } else if (!photoName.trim()) {
          setPhotoNameError("Photo name is required.");
          return;
        }
        setPhotoNameError("");

        const fpProjectId = taskData?.projectId || "";
        const fpProjectName = taskData?.projectName || "";
        const uploadedPhotos = [];

        for (let i = 0; i < validPhotoFiles.length; i++) {
          const { file, id: pendingId } = validPhotoFiles[i];
          setPhotoUploadProgress((prev) => ({
            ...prev,
            [pendingId]: { status: "uploading", progress: 0 },
          }));

          const uploadContext = buildUploadContext({
            projectId: fpProjectId,
            projectName: fpProjectName,
            category: "progress_photo",
            categoryLabel: "Progress Photo",
            sourceTable: "files_photos",
            uploadedByName: teamMemberName,
            extras: {
              task_id: taskId,
            },
            file,
          });

          const cloudinaryResult = await uploadFile(file, {
            projectId: fpProjectId,
            sourceTable: "files_photos",
            category: "progress_photo",
            uploadContext,
            onProgress: (progress) => {
              setPhotoUploadProgress((prev) => ({
                ...prev,
                [pendingId]: { status: "uploading", progress },
              }));
            },
          });

          let displayName;
          if (individualPhotoNames) {
            displayName = photoFileNames[pendingId]?.trim() || photoName.trim();
          } else {
            displayName =
              validPhotoFiles.length > 1
                ? `${photoName.trim()} ${i + 1}`
                : photoName.trim();
          }

          uploadedPhotos.push({
            fileName: displayName,
            url: cloudinaryResult.url,
            filename: file.name,
          });

          setPhotoUploadProgress((prev) => ({
            ...prev,
            [pendingId]: { status: "success", progress: 100 },
          }));
        }

        const createProgressPhotoBatch = httpsCallable(
          functions,
          "createProgressPhotoBatch",
        );
        const batchResult = await createProgressPhotoBatch({
          projectId: fpProjectId,
          taskId,
          phase: formData.constructionPhase || null,
          photos: uploadedPhotos,
          notes: null,
        });

        if (!batchResult.data?.success) {
          throw new Error(
            batchResult.data?.error || "Failed to save Files & Photos records",
          );
        }

        queryClient.invalidateQueries({
          queryKey: ["progress-photos", fpProjectId],
        });
        queryClient.invalidateQueries({ queryKey: ["task-edit", taskId] });
        queryClient.invalidateQueries({ queryKey: ["task", taskId] });
      }

      setIsDirty(false);
      if (onSave) onSave();
      onClose();
    } catch (error) {
      console.error("Error saving task:", error);
      // Error is handled by mutation's error state
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!isEditMode) return;

    try {
      await deleteTask.mutateAsync(taskId);
      setShowDeleteConfirm(false);
      if (onDelete) onDelete();
      onClose();
    } catch (error) {
      console.error("Error deleting task:", error);
    }
  };

  // Handle close with unsaved changes warning
  const handleClose = () => {
    if (isDirty) {
      if (
        window.confirm(
          "You have unsaved changes. Are you sure you want to close?",
        )
      ) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  // Get project name for edit mode display
  const projectName = taskData?.projectName || "";

  // Loading and error states
  // Only wait for projects list when in create mode without a prefilled projectId
  const needsProjectList = !isEditMode && !projectId;
  const isLoading =
    (isEditMode && taskLoading) ||
    vendorsLoading ||
    (needsProjectList && projectsLoading);
  const isUploading =
    Object.values(uploadProgress).some((p) => p.status === "uploading") ||
    Object.values(photoUploadProgress).some((p) => p.status === "uploading");
  const isSaving = updateTask.isPending || createTask.isPending || isUploading;

  // Task-only attachments (ATTACHMENTS field on task record)
  const existingAttachments = taskData?.attachments || [];
  // Linked Files & Photos records (also surface in Progress Photos)
  const existingFilesAndPhotos = taskData?.filesAndPhotos || [];
  const saveError = updateTask.error || createTask.error;
  const deleteError = deleteTask.error;

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div className="task-modal-overlay" onClick={handleClose}>
      <div className="task-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="task-modal-header">
          <div className="task-modal-title-row">
            <div className="task-modal-header-icon">
              {isEditMode ? <FaEdit /> : <FaPlus />}
            </div>
            <h3>{isEditMode ? "Edit Task" : "Create Task"}</h3>
          </div>
          <button
            className="task-modal-close"
            onClick={handleClose}
            disabled={isSaving}
            aria-label="Close modal"
          >
            <FaTimes />
          </button>
        </div>

        {/* Content */}
        <div className="task-modal-content">
          {isLoading ? (
            <div className="task-modal-loading">
              <FaSpinner className="task-modal-spinner" />
              <span>Loading task details...</span>
            </div>
          ) : taskError ? (
            <div className="task-modal-error">
              <FaExclamationTriangle />
              <span>Error loading task: {taskError.message}</span>
            </div>
          ) : (
            <form className="task-modal-form" onSubmit={handleSubmit}>
              {/* Project Field - Read-only in edit mode or when prefilled, selector in create mode */}
              <div className="task-modal-form-group">
                <label>
                  <FaHome /> Project {!isEditMode && !projectId && "*"}
                </label>
                {isEditMode ? (
                  <input
                    type="text"
                    value={projectName}
                    disabled
                    className="task-modal-readonly task-modal-project-display"
                  />
                ) : projectId ? (
                  <input
                    type="text"
                    value={initialProjectName || "Selected Project"}
                    disabled
                    className="task-modal-readonly task-modal-project-display"
                  />
                ) : (
                  <select
                    value={formData.projectId}
                    onChange={(e) => handleChange("projectId", e.target.value)}
                    required
                  >
                    <option value="">Select project...</option>
                    {projects?.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              {/* Task Name */}
              <div className="task-modal-form-group">
                <label>
                  <FaClipboardList /> Task Name *
                </label>
                <input
                  type="text"
                  value={formData.taskName}
                  onChange={(e) => handleChange("taskName", e.target.value)}
                  placeholder="Enter task name..."
                  required
                />
              </div>

              {/* Status & Construction Phase Row */}
              <div className="task-modal-form-row">
                <div className="task-modal-form-group">
                  <label>
                    <FaQuestionCircle /> Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleChange("status", e.target.value)}
                  >
                    {TASK_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="task-modal-form-group">
                  <label>
                    <FaBuilding /> Construction Phase
                  </label>
                  <select
                    value={formData.constructionPhase}
                    onChange={(e) =>
                      handleChange("constructionPhase", e.target.value)
                    }
                  >
                    <option value="">Select phase...</option>
                    {CONSTRUCTION_PHASES.map((phase) => (
                      <option key={phase} value={phase}>
                        {phase}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Date Row */}
              <div className="task-modal-form-row">
                <div className="task-modal-form-group">
                  <label>
                    <FaCalendarAlt /> Start Date
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => handleChange("startDate", e.target.value)}
                  />
                </div>
                {formData.status === "Completed" ? (
                  <div className="task-modal-form-group">
                    <label>
                      <FaCalendarAlt /> Completion Date{" "}
                      <span className="task-modal-required">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.dateCompleted}
                      className={
                        dateCompletedError ? "task-modal-input-error" : ""
                      }
                      onChange={(e) =>
                        handleChange("dateCompleted", e.target.value)
                      }
                    />
                    <p className="task-modal-photo-name-hint">
                      Confirm the actual date this task was completed — not the
                      originally planned end date.
                    </p>
                    {dateCompletedError && (
                      <p className="task-modal-file-error">
                        {dateCompletedError}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="task-modal-form-group">
                    <label>
                      <FaCalendarAlt /> End Date
                    </label>
                    <input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => handleChange("endDate", e.target.value)}
                    />
                  </div>
                )}
              </div>

              {/* Include in Digest Checkbox */}
              <div className="task-modal-form-group task-modal-checkbox-group">
                <label className="task-modal-checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.includeInDigest}
                    onChange={(e) =>
                      handleChange("includeInDigest", e.target.checked)
                    }
                  />
                  Include in Weekly Digest When Task Complete? <FaNewspaper />
                </label>
              </div>

              {/* Task Description */}
              <div className="task-modal-form-group">
                <label>
                  <FaFileAlt /> Task Description
                </label>
                <textarea
                  value={formData.taskDescription}
                  onChange={(e) =>
                    handleChange("taskDescription", e.target.value)
                  }
                  placeholder="Detailed task description..."
                  rows={3}
                />
              </div>

              {/* Task Notes (for client) */}
              <div className="task-modal-form-group">
                <label>
                  <FaStickyNote /> Task Notes for Client
                </label>
                <textarea
                  value={formData.taskNotes}
                  onChange={(e) => handleChange("taskNotes", e.target.value)}
                  placeholder="Notes visible to client..."
                  rows={3}
                />
              </div>

              {/* Internal Notes */}
              <div className="task-modal-form-group">
                <label>
                  <FaLock /> Internal Notes
                </label>
                <textarea
                  value={formData.internalNotes}
                  onChange={(e) =>
                    handleChange("internalNotes", e.target.value)
                  }
                  placeholder="Private notes (not visible to client)..."
                  rows={3}
                />
              </div>

              {/* Vendor Selection */}
              <div className="task-modal-form-group">
                <label>
                  <FaHardHat /> Assigned Vendor
                </label>
                <select
                  value={formData.assignedVendor}
                  onChange={(e) =>
                    handleChange("assignedVendor", e.target.value)
                  }
                >
                  <option value="">Select vendor...</option>
                  {vendors?.map((vendor) => (
                    <option key={vendor.id} value={vendor.id}>
                      {vendor.vendorName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Work Type (read-only, from vendor) */}
              {taskData?.workType && (
                <div className="task-modal-form-group">
                  <label>Work Type</label>
                  <input
                    type="text"
                    value={taskData.workType}
                    disabled
                    className="task-modal-readonly"
                  />
                </div>
              )}

              {/* Attachments + Files & Photos: edit mode only */}
              {!isEditMode ? (
                <div className="task-modal-form-group">
                  <div className="task-modal-attachments-note">
                    <FaInfoCircle />
                    <span>
                      Attachments and Files & Photos can be added after creating
                      the task. Save this task first, then reopen it to upload.
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  {/* Attachments (task-only, PDF/JPG/PNG) */}
                  <div className="task-modal-form-group">
                    <label>
                      <FaPaperclip /> Related Documents
                      <span className="task-modal-label-hint">(task only)</span>
                    </label>
                    <div className="task-modal-attachments">
                      {existingAttachments.length > 0 && (
                        <div className="task-modal-attachment-grid">
                          {existingAttachments.map((file, idx) => {
                            const isImage = file.type?.startsWith("image/");
                            const thumbnailUrl =
                              file.thumbnails?.large?.url ||
                              file.thumbnails?.small?.url;
                            return (
                              <a
                                key={`attachment-${idx}`}
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="task-modal-attachment-item"
                                title={file.filename}
                              >
                                {isImage && thumbnailUrl ? (
                                  <img
                                    src={thumbnailUrl}
                                    alt={file.filename}
                                    className="task-modal-attachment-thumb"
                                  />
                                ) : (
                                  <div className="task-modal-attachment-file">
                                    <FaFileAlt />
                                  </div>
                                )}
                                <span className="task-modal-attachment-name">
                                  {file.filename}
                                </span>
                              </a>
                            );
                          })}
                        </div>
                      )}

                      {pendingFiles.length > 0 && (
                        <div className="task-modal-pending-section">
                          <div className="task-modal-pending-header">
                            <span>
                              {pendingFiles.length} file
                              {pendingFiles.length !== 1 ? "s" : ""} to upload
                            </span>
                            <button
                              type="button"
                              className="task-modal-clear-all"
                              onClick={() => {
                                pendingFiles.forEach((f) => {
                                  if (f.preview) URL.revokeObjectURL(f.preview);
                                });
                                setPendingFiles([]);
                              }}
                            >
                              Clear all
                            </button>
                          </div>
                          <div className="task-modal-attachment-grid">
                            {pendingFiles.map((pf) => (
                              <div
                                key={pf.id}
                                className={`task-modal-attachment-item task-modal-pending-item ${pf.error ? "task-modal-pending-error" : ""}`}
                              >
                                {pf.preview ? (
                                  <img
                                    src={pf.preview}
                                    alt={pf.file.name}
                                    className="task-modal-attachment-thumb"
                                  />
                                ) : (
                                  <div className="task-modal-attachment-file">
                                    <FaFileAlt />
                                  </div>
                                )}
                                <span className="task-modal-attachment-name">
                                  {pf.file.name}
                                </span>
                                <span className="task-modal-attachment-size">
                                  {formatFileSize(pf.file.size)}
                                </span>
                                {pf.error && (
                                  <span className="task-modal-file-error">
                                    {pf.error}
                                  </span>
                                )}
                                {uploadProgress[pf.id]?.status ===
                                  "uploading" && (
                                  <div className="task-modal-upload-progress">
                                    <div
                                      className="task-modal-upload-progress-bar"
                                      style={{
                                        width: `${uploadProgress[pf.id]?.progress || 0}%`,
                                      }}
                                    />
                                  </div>
                                )}
                                <button
                                  type="button"
                                  className="task-modal-pending-remove"
                                  onClick={() => removePendingFile(pf.id)}
                                  disabled={isSaving}
                                  title="Remove file"
                                >
                                  <FaTimes />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div
                        className={`task-modal-dropzone ${isDragging ? "task-modal-dropzone-dragging" : ""} ${isSaving ? "task-modal-dropzone-disabled" : ""}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() =>
                          !isSaving && fileInputRef.current?.click()
                        }
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          multiple
                          accept={ATTACHMENT_ACCEPT}
                          className="task-modal-file-input"
                          onChange={(e) => {
                            if (e.target.files?.length > 0) {
                              handleFilesSelected(e.target.files);
                              e.target.value = "";
                            }
                          }}
                          disabled={isSaving}
                        />
                        <div className="task-modal-dropzone-content">
                          <FaCloudUploadAlt className="task-modal-dropzone-icon" />
                          <p className="task-modal-dropzone-text">
                            Drag & drop files here, or click to browse
                          </p>
                          <p className="task-modal-dropzone-hint">
                            PDF, JPG, or PNG up to 10MB each
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Files & Photos (images also surface in Progress Photos) */}
                  <div className="task-modal-group-divider"></div>
                  <div className="task-modal-form-group">
                    <label>
                      <FaImage /> Task Photos
                      <span className="task-modal-label-hint">
                        (images appear in Progress Photos)
                      </span>
                    </label>

                    {/* Pending Photos List */}
                    <div className="task-modal-attachments">
                      {pendingPhotoFiles.length > 0 && (
                        <div className="task-modal-pending-section">
                          <div className="task-modal-pending-header">
                            <span>
                              {pendingPhotoFiles.length} photo
                              {pendingPhotoFiles.length !== 1 ? "s" : ""} to
                              upload
                            </span>
                            <button
                              type="button"
                              className="task-modal-clear-all"
                              onClick={() => {
                                pendingPhotoFiles.forEach((f) => {
                                  if (f.preview) URL.revokeObjectURL(f.preview);
                                });
                                setPendingPhotoFiles([]);
                              }}
                            >
                              Clear all
                            </button>
                          </div>
                          <div className="task-modal-attachment-grid">
                            {pendingPhotoFiles.map((pf) => (
                              <div
                                key={pf.id}
                                className={`task-modal-attachment-item task-modal-pending-item ${pf.error ? "task-modal-pending-error" : ""}`}
                              >
                                {pf.preview ? (
                                  <img
                                    src={pf.preview}
                                    alt={pf.file.name}
                                    className="task-modal-attachment-thumb"
                                  />
                                ) : (
                                  <div className="task-modal-attachment-file">
                                    <FaImage />
                                  </div>
                                )}
                                <span className="task-modal-attachment-name">
                                  {pf.file.name}
                                </span>
                                <span className="task-modal-attachment-size">
                                  {formatFileSize(pf.file.size)}
                                </span>
                                {individualPhotoNames && !isSaving && (
                                  <input
                                    type="text"
                                    className="task-modal-individual-name-input"
                                    value={photoFileNames[pf.id] || ""}
                                    onChange={(e) => {
                                      setPhotoFileNames((prev) => ({
                                        ...prev,
                                        [pf.id]: e.target.value,
                                      }));
                                      if (photoNameError) setPhotoNameError("");
                                    }}
                                    placeholder="Photo name..."
                                  />
                                )}
                                {pf.error && (
                                  <span className="task-modal-file-error">
                                    {pf.error}
                                  </span>
                                )}
                                {photoUploadProgress[pf.id]?.status ===
                                  "uploading" && (
                                  <div className="task-modal-upload-progress">
                                    <div
                                      className="task-modal-upload-progress-bar"
                                      style={{
                                        width: `${photoUploadProgress[pf.id]?.progress || 0}%`,
                                      }}
                                    />
                                  </div>
                                )}
                                <button
                                  type="button"
                                  className="task-modal-pending-remove"
                                  onClick={() => removePendingPhotoFile(pf.id)}
                                  disabled={isSaving}
                                  title="Remove photo"
                                >
                                  <FaTimes />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Photo Name Field (populates File/Photo Name) */}
                      {pendingPhotoFiles.length > 0 && (
                        <div className="task-modal-photo-name-field">
                          <label className="task-modal-photo-name-label">
                            <FaEdit /> File/Photo Name
                            <span className="task-modal-required">*</span>
                          </label>
                          <input
                            type="text"
                            value={photoName}
                            onChange={(e) => {
                              setPhotoName(e.target.value);
                              if (photoNameError) setPhotoNameError("");
                            }}
                            placeholder="e.g., Foundation Pour Complete"
                            disabled={isSaving || individualPhotoNames}
                          />
                        </div>
                      )}
                      {pendingPhotoFiles.length > 1 && (
                        <>
                          {individualPhotoNames ? (
                            <p className="task-modal-photo-name-hint">
                              Name each photo individually using the inputs
                              above.
                            </p>
                          ) : (
                            photoName.trim() && (
                              <p className="task-modal-photo-name-hint">
                                Photos will be named: "{photoName.trim()} 1", "
                                {photoName.trim()} 2", etc.
                              </p>
                            )
                          )}
                          <div className="task-modal-individual-checkbox">
                            <input
                              type="checkbox"
                              id="task-modal-individual-names"
                              checked={individualPhotoNames}
                              onChange={(e) =>
                                setIndividualPhotoNames(e.target.checked)
                              }
                              disabled={isSaving}
                            />
                            <label htmlFor="task-modal-individual-names">
                              Name photos individually
                            </label>
                          </div>
                        </>
                      )}
                      {photoNameError && (
                        <p className="task-modal-file-error">
                          {photoNameError}
                        </p>
                      )}
                    </div>

                    <div
                      className={`task-modal-dropzone ${isDraggingPhotos ? "task-modal-dropzone-dragging" : ""} ${isSaving ? "task-modal-dropzone-disabled" : ""}`}
                      onDragOver={handlePhotoDragOver}
                      onDragLeave={handlePhotoDragLeave}
                      onDrop={handlePhotoDrop}
                      onClick={() =>
                        !isSaving && photoInputRef.current?.click()
                      }
                    >
                      <input
                        ref={photoInputRef}
                        type="file"
                        multiple
                        accept={FILES_PHOTOS_ACCEPT}
                        className="task-modal-file-input"
                        onChange={(e) => {
                          if (e.target.files?.length > 0) {
                            handlePhotoFilesSelected(e.target.files);
                            e.target.value = "";
                          }
                        }}
                        disabled={isSaving}
                      />
                      <div className="task-modal-dropzone-content">
                        <FaCloudUploadAlt className="task-modal-dropzone-icon" />
                        <p className="task-modal-dropzone-text">
                          Drag & drop images here, or click to browse
                        </p>
                        <p className="task-modal-dropzone-hint">
                          JPG, PNG, WEBP, or HEIC up to 10MB each
                        </p>
                      </div>
                    </div>
                    <div className="task-modal-form-group">
                      {existingFilesAndPhotos.length > 0 && (
                        <>
                          <label>Existing Photos:</label>
                          <div className="task-modal-attachment-grid">
                            {existingFilesAndPhotos.map((file, idx) => {
                              const isImage = file.type?.startsWith("image/");
                              const thumbnailUrl =
                                file.thumbnails?.large?.url ||
                                file.thumbnails?.small?.url;
                              return (
                                <a
                                  key={`fp-${idx}`}
                                  href={file.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="task-modal-attachment-item"
                                  title={file.filename}
                                >
                                  {isImage && thumbnailUrl ? (
                                    <img
                                      src={thumbnailUrl}
                                      alt={file.filename}
                                      className="task-modal-attachment-thumb"
                                    />
                                  ) : (
                                    <div className="task-modal-attachment-file">
                                      <FaFileAlt />
                                    </div>
                                  )}
                                  <span className="task-modal-attachment-name">
                                    {file.filename}
                                  </span>
                                  <span className="task-modal-attachment-badge">
                                    Files & Photos
                                  </span>
                                </a>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Error Display */}
              {saveError && (
                <div className="task-modal-save-error">
                  <FaExclamationTriangle />
                  <span>Error saving: {saveError.message}</span>
                </div>
              )}
              {deleteError && (
                <div className="task-modal-save-error">
                  <FaExclamationTriangle />
                  <span>Error deleting: {deleteError.message}</span>
                </div>
              )}
            </form>
          )}
        </div>

        {/* Footer */}
        {!isLoading && !taskError && (
          <div className="task-modal-footer">
            {isEditMode && (
              <button
                type="button"
                className="task-modal-btn task-modal-btn-danger"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isSaving}
              >
                <FaTrash /> Delete
              </button>
            )}
            <div className="task-modal-footer-right">
              <button
                type="button"
                className="task-modal-btn task-modal-btn-secondary"
                onClick={handleClose}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="task-modal-btn task-modal-btn-primary"
                onClick={handleSubmit}
                disabled={isSaving || !formData.taskName}
              >
                {isSaving ? (
                  <>
                    <FaSpinner className="task-modal-btn-spinner" /> Saving...
                  </>
                ) : (
                  <>
                    {isEditMode ? <FaCheckCircle /> : <FaPlusCircle />}{" "}
                    {isEditMode ? "Save Changes" : "Create Task"}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          title="Delete Task?"
          message="Are you sure you want to delete the task:"
          item={formData.taskName}
          punc="?"
          confirmText="Delete"
          cancelText="Cancel"
          isDestructive
        />
      </div>
    </div>
  );
}

export default TaskModal;
